import { twMerge } from 'tailwind-merge';

import { useS3Image } from '../../hooks/useS3Image';
import { useUploadWithExisting } from '../../hooks/useUploadWithExisting';
import { getMediaType } from '../../utils/mediaUtils';
import { RemoveButton } from '../common/RemoveButton';
import * as Form from '../Form';
import { S3Image } from '../S3Image';

export interface UploadWithExistingProps {
  mediaType: 'image' | 'media';
  name: string;
  label?: string;
  description?: string;
  dimensions?: string;
  value: File | null;
  existingUrl: string | null;
  onChange: (file: File | null) => void;
  onRemoveExisting?: () => void;
  buttonType?: 'add' | 'upload';
  buttonClassName?: string;
  placeholderClassName?: string;
  className?: string;
  maxSize?: number;
}

function ExistingUploadPreview({
  mediaType,
  name,
  label,
  description,
  dimensions,
  existingUrl,
  placeholderClassName,
  className,
  onRemoveExisting,
}: Omit<
  UploadWithExistingProps,
  'buttonType' | 'buttonClassName' | 'maxSize' | 'onRemoveExisting'
> & {
  onRemoveExisting: () => void;
}) {
  const resolvedMediaType =
    mediaType === 'image' ? 'image' : getMediaType(existingUrl);
  const { imageUrl: videoUrl, isLoading: isVideoLoading } = useS3Image(
    resolvedMediaType === 'video' ? existingUrl : null,
  );

  return (
    <div className={twMerge('flex-1 flex flex-col gap-1', className)}>
      {(label || dimensions) && (
        <div className="self-stretch">
          {label && (
            <span className="text-white text-sm font-bold leading-tight">
              {label}:{' '}
            </span>
          )}
          {dimensions && (
            <span className="text-white text-sm font-normal leading-tight">
              {dimensions}
            </span>
          )}
        </div>
      )}
      {description && (
        <div className="self-stretch text-neutral-400 text-sm font-normal leading-tight">
          {description}
        </div>
      )}
      <div className={twMerge('relative', placeholderClassName)}>
        <div className="relative w-full h-full border border-neutral-700 rounded-md overflow-hidden">
          {resolvedMediaType === 'video' ? (
            isVideoLoading ? (
              <div className="animate-pulse bg-gray-700 w-full h-full flex items-center justify-center">
                <span className="text-gray-400">Loading video…</span>
              </div>
            ) : (
              <video
                controls
                autoPlay
                muted
                loop
                playsInline
                className="block w-full h-full object-cover"
              >
                <source
                  src={videoUrl || existingUrl || undefined}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
            )
          ) : (
            <S3Image
              src={existingUrl || ''}
              alt={`${label || name} preview`}
              className="block w-full h-full object-cover"
            />
          )}
          <RemoveButton
            onClick={onRemoveExisting}
            ariaLabel={`Remove ${label || name}`}
          />
        </div>
      </div>
    </div>
  );
}

export function UploadWithExisting({
  mediaType,
  name,
  label,
  description,
  dimensions,
  value,
  existingUrl,
  onChange,
  onRemoveExisting,
  buttonType = 'add',
  buttonClassName,
  placeholderClassName,
  className,
  maxSize,
}: UploadWithExistingProps) {
  const { showExisting, handleRemoveExisting } = useUploadWithExisting({
    value,
    existingUrl,
    onRemoveExisting,
  });

  if (showExisting && existingUrl) {
    return (
      <ExistingUploadPreview
        mediaType={mediaType}
        name={name}
        label={label}
        description={description}
        dimensions={dimensions}
        value={value}
        existingUrl={existingUrl}
        onChange={onChange}
        onRemoveExisting={handleRemoveExisting}
        placeholderClassName={placeholderClassName}
        className={className}
      />
    );
  }

  if (mediaType === 'image') {
    return (
      <Form.ImageUploadInput
        name={name}
        label={label}
        description={description}
        dimensions={dimensions}
        value={value}
        onChange={onChange}
        buttonType={buttonType}
        buttonClassName={buttonClassName}
        placeholderClassName={placeholderClassName}
        className={className}
        maxSize={maxSize}
      />
    );
  }

  return (
    <Form.MediaUploadInput
      name={name}
      label={label}
      description={description}
      dimensions={dimensions}
      value={value}
      onChange={onChange}
      buttonType={buttonType}
      buttonClassName={buttonClassName}
      placeholderClassName={placeholderClassName}
      className={className}
    />
  );
}
